import { createSlice } from '@reduxjs/toolkit';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import qs from 'qs';
import { getFilterParams, getSortParams } from '../../utils/paginatedQueryParams';
import {
  buildUserAccessPayload,
  ROLE_INT_TO_NAME,
  ROLE_NAME_TO_INT
} from '../../utils/userAccessPayload';
import { Claim } from '../../@types/organization';
import { UserState } from '../../@types/user';
import { axiosAmApiInstance as amApi } from '../../utils/axios';
import { dispatch } from '../store';
import { translator } from 'translation/useTranslator';
import { DispatchError, rejectWithError } from '../dispatchError';

const USER_SORT_FIELD_MAP: Record<string, string> = {
  id: 'id',
  name: 'name',
  userName: 'email',
  created: 'createdOn',
  emailConfirmed: 'isEmailConfirmed',
  managementClaims: 'managementClaims'
};

const USER_FILTER_FIELD_MAP: Record<string, string> = {
  id: 'userId',
  name: 'name',
  userName: 'email',
  created: 'createdOn',
  emailConfirmed: 'isEmailConfirmed',
  managementClaims: 'managementClaim'
};

const initialState: UserState = {
  loading: {},
  users: [],
  usersTotal: 0,
  user: undefined
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // START LOADING
    startNewLoading(state, action) {
      state.loading[action.payload] = true;
    },

    // END LOADING
    endNewLoading(state, action) {
      state.loading[action.payload] = false;
    },

    // GET ORGANIZATION USERS
    getOrganizationUsersSuccess(state, action) {
      state.users = action.payload.users;
      state.usersTotal = action.payload.total;
      state.loading.users = false;
    },

    // GET ORGANIZATION USER
    getOrganizationUserSuccess(state, action) {
      state.user = action.payload;
      state.loading.user = false;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { getOrganizationUserSuccess } = slice.actions;

// Discards responses from superseded requests so a slow page/filter can't overwrite a newer one.
let latestGetOrganizationUsersRequestId = 0;

export type GetOrganizationUsersProps = {
  skip?: number;
  take?: number;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
};
export function getOrganizationUsers({
  skip = 0,
  take = 25,
  sortModel,
  filterModel
}: GetOrganizationUsersProps = {}) {
  return async () => {
    const requestId = ++latestGetOrganizationUsersRequestId;
    dispatch(slice.actions.startNewLoading('users'));
    try {
      const params = {
        skip,
        take,
        ...getSortParams(sortModel, USER_SORT_FIELD_MAP),
        ...getFilterParams(filterModel, USER_FILTER_FIELD_MAP)
      };
      const response = await amApi.get('/users', {
        params,
        paramsSerializer: (p) => qs.stringify(p, { allowDots: true })
      });

      if (requestId !== latestGetOrganizationUsersRequestId) {
        return await Promise.resolve({
          result: response,
          defaultSuccessMessage:
            translator?.organizationUsersFetched() ?? 'Organization users fetched'
        });
      }

      const mappedUsers = (response.data.items ?? response.data.users ?? []).map((u: any) => ({
        ...u,
        userName: u.email,
        created: u.createdOn,
        emailConfirmed: u.isEmailConfirmed ?? false,
        active: !u.isDeleted,
        roleName: typeof u.role === 'number' ? ROLE_INT_TO_NAME[u.role] ?? 'CompanyUser' : u.role,
        claims: (u.claims || []).map((c: any) => ({
          claimType: c.type ?? c.claimType,
          claimValue: c.value ?? c.claimValue
        }))
      }));
      dispatch(
        slice.actions.getOrganizationUsersSuccess({
          users: mappedUsers,
          total: response.data.total ?? mappedUsers.length
        })
      );
      dispatch(slice.actions.endNewLoading('users'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.organizationUsersFetched() ?? 'Organization users fetched'
      });
    } catch (error) {
      if (requestId !== latestGetOrganizationUsersRequestId) {
        return await Promise.resolve({
          defaultSuccessMessage:
            translator?.organizationUsersFetched() ?? 'Organization users fetched'
        });
      }

      dispatch(slice.actions.endNewLoading('users'));

      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchOrganizationUsers() ?? 'Could not fetch organization users'
      });
    }
  };
}

export function getOrganizationUser(userId?: string) {
  return async () => {
    if (!userId) {
      dispatch(slice.actions.getOrganizationUserSuccess(undefined));
      return {
        defaultSuccessMessage: translator?.organizationUserReset() ?? 'Organization user reset'
      };
    }

    dispatch(slice.actions.startNewLoading('user'));
    try {
      const response = await amApi.get(`/users/${userId}`);
      const userData = response.data;
      const mappedUser = {
        ...userData,
        userId: userData.id,
        userName: userData.email,
        created: userData.createdOn,
        emailConfirmed: userData.isEmailConfirmed ?? false,
        active: !userData.isDeleted,
        roleName:
          typeof userData.role === 'number'
            ? ROLE_INT_TO_NAME[userData.role] ?? 'CompanyUser'
            : userData.role,
        claims: (userData.claims || []).map((c: any) => ({
          claimType: c.type ?? c.claimType,
          claimValue: c.value ?? c.claimValue
        }))
      };
      dispatch(slice.actions.getOrganizationUserSuccess(mappedUser));
      dispatch(slice.actions.endNewLoading('user'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.organizationUserFetched() ?? 'Organization user fetched'
      });
    } catch (error) {
      dispatch(slice.actions.endNewLoading('user'));

      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchOrganizationUser() ?? 'Could not fetch organization user'
      });
    }
  };
}

export function addUser(user: any) {
  dispatch(slice.actions.startNewLoading('user'));
  return async () => {
    let userAlreadyExists = false;
    const userAccessPayload = buildUserAccessPayload(user.role, user.claims || []);

    const response = await amApi
      .post('/v2/users', {
        email: user.email
      })
      .catch((error: any) => {
        if (
          error.type === 'Validation.User' &&
          error.detail === `Username '${user.email}' is already taken.`
        ) {
          userAlreadyExists = true;
          return null;
        }
        dispatch(slice.actions.endNewLoading('user'));
        return rejectWithError({
          error: error,
          defaultErrorMessage: translator?.couldNotAddUser() ?? `Could not add user`
        });
      });

    if (userAlreadyExists) {
      await amApi
        .post(`/tenants/access?email=${encodeURIComponent(user.email)}`, userAccessPayload)
        .catch((error: any) => {
          dispatch(slice.actions.endNewLoading('user'));
          throw new DispatchError({
            error: error,
            defaultErrorMessage: translator?.couldNotAddUser() ?? `Could not add user`
          });
        });
    } else {
      await amApi
        .post(`/tenants/access/${response!.data.userId}`, userAccessPayload)
        .catch((error: any) => {
          dispatch(slice.actions.endNewLoading('user'));
          throw new DispatchError({
            error: error,
            defaultErrorMessage: translator?.couldNotAddUser() ?? `Could not add user`
          });
        });
    }

    dispatch(slice.actions.endNewLoading('user'));

    return {
      defaultSuccessMessage: translator?.userAdded() ?? 'User added'
    };
  };
}

export type UpdateUserProps = {
  userId: string;
  role: string;
  claims: Claim[];
  name?: string;
  address?: string;
  canUpdateProfile?: boolean;
  canAssignClaims?: boolean;
};
export function updateUser({
  userId,
  role,
  claims,
  name,
  address,
  canUpdateProfile,
  canAssignClaims
}: UpdateUserProps) {
  dispatch(slice.actions.startNewLoading('user'));
  return async () => {
    try {
      if (canUpdateProfile) {
        await amApi.put(`/v2/users/${userId}`, {
          name,
          address
        });
      }

      if (canAssignClaims) {
        const roleInt = ROLE_NAME_TO_INT[role] ?? 2;
        await amApi.put(`/users/${userId}/claims`, buildUserAccessPayload(roleInt, claims));
      }

      dispatch(slice.actions.endNewLoading('user'));
      return await Promise.resolve({
        defaultSuccessMessage: translator?.userRoleUpdated() ?? 'User updated'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('user'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: 'Could not update user'
      });
    }
  };
}

type ResendUserInviteProps = {
  userId: string;
  userEmail: string;
};
export function resendUserInvite({ userId, userEmail }: ResendUserInviteProps) {
  const loadingId = `userInvite${userId}`;
  dispatch(slice.actions.startNewLoading(loadingId));
  return async () => {
    try {
      const response = await amApi.post(`/authorization/reinvite/${userId}`);
      dispatch(slice.actions.endNewLoading(loadingId));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.inviteSentTo(userEmail) ?? `Invite sent to ${userEmail}`
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading(loadingId));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotSendInviteTo(userEmail) ?? `Could not send invite to ${userEmail}`
      });
    }
  };
}

export function deleteUsers(ids: string[]) {
  dispatch(slice.actions.startNewLoading('user'));
  return async () => {
    try {
      await Promise.all(ids.map((id) => amApi.delete('/tenants/access/' + id)));

      dispatch(slice.actions.endNewLoading('user'));

      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.usersDeleted(`${ids.length}`) ??
          'User' + (ids.length >= 2 ? 's' : '') + ' deleted'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('user'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotDeleteUsers() ?? `Could not delete users`
      });
    }
  };
}
